import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { accountSummaryService } from "../services/accountSummary.service";
import { getCache, setCache, generateCacheKey } from "../helper/cache";
import { triggerRefresh } from "./refreshSlice";
import { toast } from "../common_components/Toast/ToastUtils";
// Initial State
const initialState = {
    allClients: [],
    allClientsLoading: false,
    allClientsError: null,
    singleClient: null,
    singleClientLoading: false,
    singleClientError: null,
    lastUpdated: null,
    cronStatus: null,
    cronLoading: false,
    cronError: null,
    selectedDateRange: {
        startDate: "",
        endDate: "",
    },
    performanceReport: null,
    performanceReportLoading: false,
    performanceReportError: null,
    performanceEntities: [],
    performanceEntitiesLoading: false,
    performanceEntitiesError: null,
    visibleClients: [],
    visibleClientsLoading: false,
    visibleClientsError: null,
};
// Async Thunks
export const fetchAllAccountSummary = createAsyncThunk("accountSummary/fetchAll", async (params, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token || "";
        const response = await accountSummaryService.getAllAccountSummary(params, token);
        return response;
    }
    catch (err) {
        return rejectWithValue(err.message || "Failed to fetch all clients summary");
    }
});
export const fetchSingleAccountSummary = createAsyncThunk("accountSummary/fetchSingle", async ({ clientId, ...params }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token || "";
        const response = await accountSummaryService.getSingleAccountSummary(clientId, params, token);
        return response;
    }
    catch (err) {
        return rejectWithValue(err.message || "Failed to fetch single client summary");
    }
});
export const refreshAccountSummary = createAsyncThunk("accountSummary/refreshAccountSummary", async (_, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token || "";
        const response = await accountSummaryService.refreshAccountSummary(token);
        return response;
    }
    catch (error) {
        console.error("refreshAccountSummary error:", error);
        return rejectWithValue(error?.response?.data?.message || error.message || "Failed to refresh account summary");
    }
});
// Triggers a live provider resync (/api/refresh, module_key=account_summary) for one
// client + date range — distinct from `refreshAccountSummary`/`refreshData:true`, which
// only bypasses this API's own cache and re-aggregates already-stored data.
export const refreshClientAccountSummary = createAsyncThunk("accountSummary/refreshClientAccountSummary", async ({ clientId, startDate, endDate }, { dispatch, rejectWithValue }) => {
    const result = await dispatch(triggerRefresh({
        clientId,
        dateRange: [{ startDate, endDate }],
        moduleKey: "account_summary",
    }));
    if (triggerRefresh.fulfilled.match(result)) {
        const failedNetworks = result.payload?.data?.failed_networks || [];
        if (failedNetworks.length > 0) {
            const failedNames = [...new Map(failedNetworks.map((n) => [n.network, n.error])).entries()]
                .map(([name, err]) => {
                let eStr = typeof err === 'string' ? err : err?.message;
                if (eStr && eStr.toLowerCase().includes('time'))
                    eStr = 'Timeout';
                return `${name.charAt(0).toUpperCase() + name.slice(1)}${eStr ? ` (${eStr})` : ''}`;
            })
                .join(', ');
            toast.warning(`Account Summary refresh completed.\nFailed to refresh: ${failedNames}.`);
        }
        else {
            toast.success("Account Summary refresh completed successfully.");
        }
        return result.payload?.data ?? result.payload;
    }
    return rejectWithValue(result.payload || "Failed to refresh account summary");
});
export const triggerHourlyCron = createAsyncThunk("accountSummary/triggerHourlyCron", async (params, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token || "";
        const response = await accountSummaryService.triggerHourlyCron(params, token);
        return response;
    }
    catch (error) {
        console.error("triggerHourlyCron error:", error);
        return rejectWithValue(error?.response?.data?.message || error.message || "Failed to trigger hourly cron");
    }
});
export const getCronStatus = createAsyncThunk("accountSummary/getCronStatus", async (_, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token || "";
        const response = await accountSummaryService.getCronStatus(token);
        return response.data;
    }
    catch (error) {
        console.error("getCronStatus error:", error);
        return rejectWithValue(error?.response?.data?.message || error.message || "Failed to fetch cron status");
    }
});
export const fetchPerformanceReport = createAsyncThunk("performance/report", async (params, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token || "";
        const cacheKey = generateCacheKey("sport_performance", params);
        const cachedData = getCache(cacheKey);
        if (cachedData)
            return cachedData;
        const response = await accountSummaryService.getSportPerformanceReport(params, token);
        setCache(cacheKey, response, 300);
        return response;
    }
    catch (error) {
        console.error("fetchPerformanceReport error:", error);
        return rejectWithValue(error?.response?.data?.message || error.message || "Failed to fetch performance report");
    }
});
export const fetchPerformanceEntities = createAsyncThunk("accountSummary/fetchPerformanceEntities", async (params, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token || "";
        const response = await accountSummaryService.getPerformanceEntities(params, token);
        return response;
    }
    catch (error) {
        console.error("fetchPerformanceEntities error:", error);
        return rejectWithValue(error?.response?.data?.message || error.message || "Failed to fetch performance entities");
    }
});
export const fetchVisibleClients = createAsyncThunk("accountSummary/fetchVisibleClients", async (arg, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token || "";
        const moduleKey = arg?.moduleKey || "account_summary";
        const status = arg?.status || 'active';
        const month = arg?.month;
        const response = await accountSummaryService.getVisibleClients(token, moduleKey, status, month);
        return response.data; // (ya return response) -> aapke API shape pe depend
    }
    catch (err) {
        return rejectWithValue(err.message || "Failed to fetch visible clients");
    }
});
export const hideClients = createAsyncThunk("accountSummary/hideClients", async ({ client_ids, reason, moduleKey }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token || "";
        return await accountSummaryService.hideClients(client_ids, reason || null, token, moduleKey || "account_summary");
    }
    catch (err) {
        return rejectWithValue(err.message || "Failed to hide clients");
    }
});
export const unhideClient = createAsyncThunk("accountSummary/unhideClient", async (arg, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token || "";
        const clientId = typeof arg === "string" ? arg : arg.clientId;
        const client_ids = typeof arg === "string" ? undefined : arg.client_ids;
        const moduleKey = typeof arg === "string" ? undefined : arg.moduleKey;
        return await accountSummaryService.unhideClient(clientId || null, token, moduleKey, client_ids);
    }
    catch (err) {
        return rejectWithValue(err.message || "Failed to unhide client");
    }
});
// Slice
const accountSummarySlice = createSlice({
    name: "accountSummary",
    initialState,
    reducers: {
        setDateRange: (state, action) => {
            state.selectedDateRange = action.payload;
        },
        clearAllClientsData: (state) => {
            state.allClients = [];
            state.allClientsError = null;
        },
        clearSingleClientData: (state) => {
            state.singleClient = null;
            state.singleClientError = null;
        },
        clearAccountSummaryData: (state) => {
            state.allClients = [];
            state.singleClient = null;
            state.allClientsError = null;
            state.singleClientError = null;
            state.lastUpdated = null;
        },
        clearAccountSummaryError: (state) => {
            state.allClientsError = null;
            state.singleClientError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // All Clients - Separate loading states
            .addCase(fetchAllAccountSummary.pending, (state) => {
            state.allClientsLoading = true;
            state.allClientsError = null;
        })
            .addCase(fetchAllAccountSummary.fulfilled, (state, action) => {
            state.allClientsLoading = false;
            state.allClients = Array.isArray(action.payload.data) ? action.payload.data : [];
            state.lastUpdated = action.payload.last_updated || null;
        })
            .addCase(fetchAllAccountSummary.rejected, (state, action) => {
            state.allClientsLoading = false;
            state.allClientsError = action.payload;
        })
            // Single Client - Separate loading states
            .addCase(fetchSingleAccountSummary.pending, (state) => {
            state.singleClientLoading = true;
            state.singleClientError = null;
        })
            .addCase(fetchSingleAccountSummary.fulfilled, (state, action) => {
            state.singleClientLoading = false;
            state.singleClient = action.payload.data?.[0] || null;
            // Note: intentionally NOT touching state.lastUpdated here — that field feeds
            // the page-level "Last updated" (driven by fetchAllAccountSummary only). Each
            // expanded client row tracks its own last_updated locally (useClientRowState).
        })
            .addCase(fetchSingleAccountSummary.rejected, (state, action) => {
            state.singleClientLoading = false;
            state.singleClientError = action.payload;
        })
            // Refresh Account Summary
            .addCase(refreshAccountSummary.pending, (state) => {
            state.allClientsLoading = true;
            state.allClientsError = null;
        })
            .addCase(refreshAccountSummary.fulfilled, (state) => {
            state.allClientsLoading = false;
            state.allClientsError = null;
        })
            .addCase(refreshAccountSummary.rejected, (state, action) => {
            state.allClientsLoading = false;
            state.allClientsError = action.payload || "Failed to refresh account summary";
        })
            // Trigger Hourly Cron
            .addCase(triggerHourlyCron.pending, (state) => {
            state.cronLoading = true;
            state.cronError = null;
        })
            .addCase(triggerHourlyCron.fulfilled, (state) => {
            state.cronLoading = false;
            state.cronError = null;
        })
            .addCase(triggerHourlyCron.rejected, (state, action) => {
            state.cronLoading = false;
            state.cronError = action.payload || "Failed to trigger hourly cron";
        })
            // Get Cron Status
            .addCase(getCronStatus.pending, (state) => {
            state.cronLoading = true;
            state.cronError = null;
        })
            .addCase(getCronStatus.fulfilled, (state, action) => {
            state.cronLoading = false;
            state.cronStatus = action.payload;
            state.cronError = null;
        })
            .addCase(getCronStatus.rejected, (state, action) => {
            state.cronLoading = false;
            state.cronError = action.payload || "Failed to fetch cron status";
        })
            // Performance Report
            .addCase(fetchPerformanceReport.pending, (state) => {
            state.performanceReportLoading = true;
            state.performanceReportError = null;
        })
            .addCase(fetchPerformanceReport.fulfilled, (state, action) => {
            state.performanceReportLoading = false;
            state.performanceReport = action.payload.data;
            state.performanceReportError = null;
        })
            .addCase(fetchPerformanceReport.rejected, (state, action) => {
            state.performanceReportLoading = false;
            state.performanceReportError = action.payload || "Failed to fetch performance report";
        })
            // Performance Entities
            .addCase(fetchPerformanceEntities.pending, (state) => {
            state.performanceEntitiesLoading = true;
            state.performanceEntitiesError = null;
        })
            .addCase(fetchPerformanceEntities.fulfilled, (state, action) => {
            state.performanceEntitiesLoading = false;
            state.performanceEntities = action.payload.data;
            state.performanceEntitiesError = null;
        })
            .addCase(fetchPerformanceEntities.rejected, (state, action) => {
            state.performanceEntitiesLoading = false;
            state.performanceEntitiesError = action.payload || "Failed to fetch performance entities";
        })
            // Visible Clients
            .addCase(fetchVisibleClients.pending, (state) => {
            state.visibleClientsLoading = true;
            state.visibleClientsError = null;
        })
            .addCase(fetchVisibleClients.fulfilled, (state, action) => {
            state.visibleClientsLoading = false;
            state.visibleClients = action.payload;
        })
            .addCase(fetchVisibleClients.rejected, (state, action) => {
            state.visibleClientsLoading = false;
            state.visibleClientsError = action.payload;
        })
            .addCase(hideClients.fulfilled, (state) => { })
            .addCase(unhideClient.fulfilled, (state) => { });
    },
});
// Actions
export const { setDateRange, clearAllClientsData, clearSingleClientData, clearAccountSummaryData, clearAccountSummaryError, } = accountSummarySlice.actions;
// Selectors - Updated with separate loading states
export const selectAllClients = (state) => state.accountSummary.allClients;
export const selectSingleClient = (state) => state.accountSummary.singleClient;
export const selectAllClientsLoading = (state) => state.accountSummary.allClientsLoading;
export const selectSingleClientLoading = (state) => state.accountSummary.singleClientLoading;
export const selectAllClientsError = (state) => state.accountSummary.allClientsError;
export const selectSingleClientError = (state) => state.accountSummary.singleClientError;
export const selectAccountSummaryLastUpdated = (state) => state.accountSummary.lastUpdated;
export const selectCronStatus = (state) => state.accountSummary.cronStatus;
export const selectCronLoading = (state) => state.accountSummary.cronLoading;
export const selectCronError = (state) => state.accountSummary.cronError;
export const selectPerformanceReport = (state) => state.accountSummary.performanceReport;
export const selectPerformanceReportLoading = (state) => state.accountSummary.performanceReportLoading;
export const selectPerformanceReportError = (state) => state.accountSummary.performanceReportError;
export const selectPerformanceEntities = (state) => state.accountSummary.performanceEntities;
export const selectPerformanceEntitiesLoading = (state) => state.accountSummary.performanceEntitiesLoading;
export const selectPerformanceEntitiesError = (state) => state.accountSummary.performanceEntitiesError;
export const selectDateRange = (state) => state.accountSummary.selectedDateRange;
export const selectVisibleClients = (state) => state.accountSummary.visibleClients;
export const selectVisibleClientsLoading = (state) => state.accountSummary.visibleClientsLoading;
export const selectVisibleClientsError = (state) => state.accountSummary.visibleClientsError;
// Export reducer
export default accountSummarySlice.reducer;
